import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import ProductRatingInProductList from '@/components/common/product/rating/ProductRatingInProductList';
import QuantitySelectorWB from '@/components/common/QuantitySelectorWB';
import ProductImage from '@/components/product/ProductImage';
import ProductPrice from '@/components/product/ProductPrice';
import { useAppDispatch, useAppSelector } from '@/redux';
import { addBoxProduct } from '@/redux/slices/box.slice';
import { showProductDetailsModalWB } from '@/redux/slices/gui.slice';
import { addItemToStorageWB } from '@/redux/slices/storage.slice';
import { useServices } from '@/services/useServices';
import { getCriticsRatingNotes } from '@/utils/productCardUtils';
import { getAttributeId, getAttributeValue, getProductImage } from '@/utils/productUtils';
import {
	getQuantityAvailable,
	getQuantityAvailableForStorageWB,
	getQuantityToAdd,
	getSizeStock,
	getStorageItemQuantityToAddOrRemove,
	isCardDisable,
} from '@/utils/wineboxUtils';

const BadgeForAdmin = dynamic(() => import('@/components/product/BadgeForAdmin'), { ssr: false });
BadgeForAdmin.displayName = 'BadgeForAdmin';

import { CUSTOM_PAGE_VIEW_EVENT, CUSTOM_PAGE_VIEW_TYPE } from '@/constants/datalayer';
import { ATTRIBUTES } from '@/constants/product';
import { URLS } from '@/constants/urls';
import styles from './ProductCardWB.module.scss';

export const ProductCardWB = ({ product, storage = false }) => {
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const { datalayerService } = useServices();
	const storeConf = useAppSelector(({ root }) => root.storeConf);
	const nbItem = useAppSelector(({ box }) => box.content.nbItem);
	const boxItems = useAppSelector(({ box }) => box.content.products);
	const { wineboxModalOpen } = useAppSelector(({ gui }) => gui);
	const {
		products: wineBoxStorageProducts,
		nbItems: wineBoxStorageNbItems,
		isComplete: isWineBoxStorageComplete,
	} = useAppSelector(({ storage }) => storage.storageWineBox);
	const showAdminBadges = useAppSelector(({ user }) => user.settings.showAdminBadges);

	const { image, imageSize, itemsCP, productName, quantity: storageItemQuantity, itemName, itemId } = product;

	const itemCP = itemsCP?.[0];

	const [isOutOfStock, setIsOutOfStock] = useState(false);
	const [quantity, setQuantity] = useState(1);
	const [size, setSize] = useState(itemCP?.stock || storageItemQuantity);

	const url = getProductImage(image, false, 'thumb');
	const ratings = getCriticsRatingNotes(product, storeConf) || [];
	const country = getAttributeId(product, ATTRIBUTES.COUNTRY, '');

	const region = getAttributeValue(product, ATTRIBUTES.REGION, '');
	const appellation = getAttributeValue(product, ATTRIBUTES.APPELLATION, '');
	const classement = getAttributeValue(product, ATTRIBUTES.RATING, '');
	const couleur = getAttributeValue(product, ATTRIBUTES.COLOR, '');

	const appellationId = getAttributeId(product, ATTRIBUTES.APPELLATION, '');

	const countryIconUrl = URLS.COUNTRY_FLAG.replace('{0}', country);

	const isWBCardDisabled = isCardDisable(storage, isWineBoxStorageComplete, nbItem, wineboxModalOpen);

	const containerClassName = [styles.container, isWBCardDisabled && styles['opacity-card']].filter(Boolean).join(' ');

	const topClassName = [styles.top, !isWBCardDisabled && !storage && styles.cursor].filter(Boolean).join(' ');

	const customClassPrice = {
		htPrice: styles['ht-price'],
	};

	const iconedAttributes = ({ url, alt, attribute }) => (
		<span className={styles.attribute} data-test-id="card-wb-attribute">
			<Image src={url} alt={alt} width={16} height={16} className={styles.icon} />
			{attribute}
		</span>
	);

	const handleAddToBox = () => {
		if (storage) {
			const qty = getStorageItemQuantityToAddOrRemove(wineBoxStorageProducts, product, false, quantity);
			dispatch(addItemToStorageWB({ itemId, quantity: qty }));
		} else {
			const qty = getQuantityToAdd(boxItems, itemCP?.partnumber, quantity);
			dispatch(
				addBoxProduct({
					item: itemCP?.partnumber,
					qty,
					options: {
						set: true,
						showConfirm: false,
					},
				}),
			);

			if (!nbItem || nbItem === 0) {
				datalayerService.pushPageName(CUSTOM_PAGE_VIEW_EVENT.CS_WB_POPIN_OPEN_PV, CUSTOM_PAGE_VIEW_TYPE.MODAL_WINEBOX);
			}
		}
	};

	const handleOpenProductDetailModalWB = () => {
		if (!isWBCardDisabled && !storage) {
			dispatch(showProductDetailsModalWB({ product }));
		}
	};

	useEffect(() => {
		if (storage) {
			const size = getQuantityAvailableForStorageWB(wineBoxStorageProducts, product, wineBoxStorageNbItems);
			setSize(size);
		} else {
			const size = getQuantityAvailable(boxItems, itemCP, nbItem);
			const sizeStock = getSizeStock(boxItems, itemCP);
			setIsOutOfStock(sizeStock === 0);
			setSize(size);
		}
	}, [itemCP, boxItems, quantity, wineBoxStorageProducts]);

	return (
		<>
			<div className={containerClassName} data-test-id="card-wb-container" data-rbf="product-card-wb">
				<div className={topClassName} onClick={handleOpenProductDetailModalWB}>
					<div className={styles.ratings}>
						<ProductRatingInProductList ratings={ratings} />
					</div>
					<div className={styles.img}>
						<ProductImage url={url} alt={productName} sizeList={imageSize?.sizeByFolder} size="thumb" />
					</div>
					<div className={styles.flag} data-test-id="card-wb-flag">
						<Image src={countryIconUrl} alt={country} width={24} height={24} />
					</div>
				</div>

				<div className={styles.content} data-test-id="card-wb-content">
					<span className={styles.title} data-test-id="card-wb-title">
						{productName ? productName : itemName}
					</span>
					{iconedAttributes({ url: URLS.ICON_LOCATION_PIN, alt: region, attribute: region })}
					{appellation &&
						appellationId !== '0' &&
						iconedAttributes({ url: URLS.ICON_GRAPE, alt: appellation, attribute: appellation })}
					{classement && iconedAttributes({ url: URLS.ICON_STAR_LINE, alt: classement, attribute: classement })}
					{iconedAttributes({ url: URLS.ICON_WINE_GLASS, alt: couleur, attribute: couleur })}
				</div>

				<hr className={styles.separator} />

				<div className={styles.bottom}>
					{!storage && (
						<ProductPrice
							currentItem={itemCP}
							product={product}
							displayUnitBloc={false}
							customClassPrice={customClassPrice}
						/>
					)}
					<QuantitySelectorWB
						isOutOfStock={isOutOfStock}
						size={size}
						setQuantity={setQuantity}
						className={styles.selector}
					/>
				</div>
				{showAdminBadges && <BadgeForAdmin item={itemCP} className={styles.badges} />}
			</div>
			<div className={styles['button-container']}>
				<CallToActionContainer
					className={styles.button}
					disabled={isOutOfStock || isWBCardDisabled}
					onClick={handleAddToBox}
					icon={
						!isOutOfStock && {
							url: URLS.ADD_WHITE,
							alt: 'add',
						}
					}
				>
					{!isOutOfStock ? <>{t('product.addToWinebox')}</> : <>{t('product.noStock')}</>}
				</CallToActionContainer>
			</div>
		</>
	);
};

ProductCardWB.propTypes = {
	product: PropTypes.object.isRequired,
	storage: PropTypes.bool,
};

export default ProductCardWB;
